// The glossary's links: how the program's terms and teams relate. Documents
// state some ("ROPS, formerly the Bot Shop"); a person records the rest, with
// where they read it (a document page) or who said it ("client statement,
// date"). Evidence matching reads them on its next run: a successor takes
// over its predecessor's dates, and a same-as name is the team's name too.
// Adding, editing and removing need the program's execution write, the gate
// ryugu applies to AddGlossaryRelation.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraCombobox, SpecraDataTable, SpecraFormField, SpecraInput, SpecraOverlayDrawer, SpecraSectionHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../../../lib/api/apiErrorMessage';
import { useCmAccess } from '../Deliverables/access';
import { useWorkstreams } from '../Deliverables/hooks';
import { useGlossaryDocuments } from './GlossaryProvider';
import { useAddGlossaryLink, useProgramGlossary, useRemoveGlossaryLink } from './hooks';
/** The relations a person records, read "from … to". */ export const LINK_RELATIONS = [
    {
        value: 'same_as',
        label: 'is the same as'
    },
    {
        value: 'formerly',
        label: 'was formerly'
    },
    {
        value: 'successor_of',
        label: 'took over from'
    },
    {
        value: 'part_of',
        label: 'is part of'
    }
];
const RELATION_LABEL = {
    ...Object.fromEntries(LINK_RELATIONS.map((r)=>[
            r.value,
            r.label
        ])),
    covered_by: 'is covered by',
    within: 'sits within',
    split_from: 'split from'
};
export function endInput(side, value) {
    if (!value) return {};
    const at = value.indexOf(':');
    const kind = value.slice(0, at);
    const id = value.slice(at + 1);
    if (side === 'from') return kind === 'team' ? {
        fromWorkstreamId: id
    } : {
        fromTermId: id
    };
    return kind === 'team' ? {
        toWorkstreamId: id
    } : {
        toTermId: id
    };
}
/**
 * The picker's options: every team, then every term that is not a team's own
 * name (a team's term is the team).
 */ export function endOptions(terms, teams) {
    const teamOpts = teams.map((t)=>({
            value: `team:${t.id}`,
            label: t.name,
            description: 'Team',
            keywords: terms.filter((x)=>x.workstreamId === t.id).map((x)=>x.term)
        }));
    const termOpts = terms.filter((t)=>!t.workstreamId).map((t)=>({
            value: `term:${t.id}`,
            label: t.term,
            description: t.expansion || undefined
        }));
    return [
        ...teamOpts,
        ...termOpts
    ];
}
/** A stored end as a picker value: a team's term is its team. */ function endValue(termId, terms) {
    const t = terms.get(termId);
    return t?.workstreamId ? `team:${t.workstreamId}` : `term:${termId}`;
}
export function GlossaryLinks({ programId, onOpenPage }) {
    const glossary = useProgramGlossary(programId);
    const teams = useWorkstreams(programId);
    const docs = useGlossaryDocuments(programId);
    const access = useCmAccess(programId);
    const remove = useRemoveGlossaryLink(programId);
    const [editing, setEditing] = useState(null);
    const terms = useMemo(()=>new Map((glossary.data?.terms ?? []).map((t)=>[
                t.id,
                t
            ])), [
        glossary.data
    ]);
    const teamName = useMemo(()=>new Map((teams.data ?? []).map((w)=>[
                w.id,
                w.name
            ])), [
        teams.data
    ]);
    const links = glossary.data?.relations ?? [];
    const endName = (termId)=>{
        const t = terms.get(termId);
        if (!t) return '—';
        return t.workstreamId && teamName.get(t.workstreamId) || t.term;
    };
    const columns = [
        {
            id: 'link',
            header: 'Link',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium text-foreground",
                            children: endName(r.fromTermId)
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: RELATION_LABEL[r.relation] ?? r.relation
                        }),
                        ' ',
                        /*#__PURE__*/ _jsx("span", {
                            className: "font-medium text-foreground",
                            children: endName(r.toTermId)
                        })
                    ]
                })
        },
        {
            id: 'source',
            header: 'Source',
            cell: (r)=>{
                const docId = r.sourceDocumentId || r.refs[0]?.documentId || '';
                const page = r.sourcePage || r.refs[0]?.page || 0;
                const doc = docId ? docs.get(docId) : undefined;
                return /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-[var(--space-1)]",
                    children: [
                        docId ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center gap-[var(--space-2)]",
                            children: [
                                onOpenPage && doc?.fileId ? /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "ghost",
                                    className: "shrink-0",
                                    onClick: ()=>onOpenPage(docId, page || 1),
                                    children: page > 0 ? `Open page ${page}` : 'Open'
                                }) : null,
                                /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "meta",
                                    tone: "muted",
                                    className: "min-w-0 max-w-xs truncate",
                                    children: doc?.title ?? 'Source document'
                                })
                            ]
                        }) : null,
                        r.reason ? /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            children: r.reason
                        }) : null
                    ]
                });
            }
        },
        {
            id: 'by',
            header: 'Recorded by',
            className: 'whitespace-nowrap',
            cell: (r)=>r.source === 'reviewer' ? /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: [
                        r.editedByName || 'A reviewer',
                        r.createdAt ? ` · ${r.createdAt.slice(0, 10)}` : ''
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: "Read from the documents"
                })
        }
    ];
    if (access.canWrite) {
        columns.push({
            id: 'actions',
            header: '',
            className: 'whitespace-nowrap text-right',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex gap-[var(--space-1)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "ghost",
                            "data-testid": Testids.glossary.editLink,
                            onClick: ()=>setEditing(r),
                            children: "Edit"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "ghost",
                            "data-testid": Testids.glossary.removeLink,
                            disabled: remove.isPending,
                            onClick: ()=>remove.mutate(r.id, {
                                    onSuccess: ()=>toast.success('Link removed'),
                                    onError: (e)=>toast.error(apiErrorMessage(e, 'Could not remove the link'))
                                }),
                            children: "Remove"
                        })
                    ]
                })
        });
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-[var(--space-3)]",
        "data-testid": Testids.glossary.links,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: `Links (${links.length})`,
                action: access.canWrite ? /*#__PURE__*/ _jsx(SpecraButton, {
                    size: "sm",
                    variant: "secondary",
                    "data-testid": Testids.glossary.addLink,
                    onClick: ()=>setEditing('new'),
                    children: "Add link"
                }) : null
            }),
            /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "How the program's terms and teams relate. Evidence matching reads these: a team that took over from another files the other's rows from its start date on."
            }),
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                caption: "Glossary links",
                columns: columns,
                rows: links,
                getRowId: (r)=>r.id,
                loading: glossary.isLoading,
                rowTestId: Testids.glossary.linkRow,
                getRowProps: (r)=>({
                        'data-relation': r.relation
                    }),
                emptyTitle: "No links yet",
                emptyDescription: access.canWrite ? 'Add one when a team was renamed, took over another’s work, or is known by another name.' : 'Someone with execution write on this program can add links.'
            }),
            editing ? /*#__PURE__*/ _jsx(GlossaryLinkDrawer, {
                programId: programId,
                link: editing === 'new' ? undefined : editing,
                options: endOptions([
                    ...terms.values()
                ], teams.data ?? []),
                initial: editing === 'new' ? undefined : {
                    from: endValue(editing.fromTermId, terms),
                    to: endValue(editing.toTermId, terms)
                },
                documents: [
                    ...docs.entries()
                ].map(([id, d])=>({
                        id,
                        title: d.title
                    })),
                onClose: ()=>setEditing(null)
            }) : null
        ]
    });
}
function GlossaryLinkDrawer({ programId, link, options, initial, documents, onClose }) {
    const add = useAddGlossaryLink(programId);
    const [from, setFrom] = useState(initial?.from);
    const [to, setTo] = useState(initial?.to);
    const [relation, setRelation] = useState(LINK_RELATIONS.some((r)=>r.value === link?.relation) ? link?.relation ?? '' : '');
    const [documentId, setDocumentId] = useState(link?.sourceDocumentId ?? '');
    const [page, setPage] = useState(link?.sourcePage ? String(link.sourcePage) : '');
    const [statement, setStatement] = useState(link?.reason ?? '');
    const sourced = !!documentId || statement.trim() !== '';
    const ready = !!from && !!to && from !== to && !!relation && sourced;
    const save = ()=>{
        if (!ready) return;
        add.mutate({
            ...endInput('from', from),
            ...endInput('to', to),
            relation,
            reason: statement.trim(),
            sourceDocumentId: documentId,
            sourcePage: documentId && Number(page) > 0 ? Number(page) : 0,
            replacesRelationId: link?.id ?? ''
        }, {
            onSuccess: ()=>{
                toast.success(link ? 'Link updated' : 'Link added');
                onClose();
            },
            onError: (e)=>toast.error(apiErrorMessage(e, 'Could not save the link'))
        });
    };
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: true,
        width: "md",
        title: link ? 'Edit link' : 'Add link',
        description: "Record how two terms or teams relate, and where you read it.",
        testId: Testids.glossary.linkDrawer,
        onOpenChange: (next)=>{
            if (!next && !add.isPending) onClose();
        },
        footerProps: {
            confirmLabel: link ? 'Save link' : 'Add link',
            onConfirm: save,
            onCancel: onClose,
            confirmDisabled: !ready,
            loading: add.isPending,
            loadingLabel: 'Saving…'
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-[var(--space-4)]",
            children: [
                /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Term or team",
                    placeholder: "Pick a term or team",
                    searchPlaceholder: "Search terms and teams…",
                    options: options,
                    value: from,
                    onValueChange: setFrom,
                    triggerProps: {
                        'data-testid': Testids.glossary.linkFrom
                    }
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Relation",
                    htmlFor: "glossary-link-relation",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        id: "glossary-link-relation",
                        value: relation,
                        onChange: (e)=>setRelation(e.target.value),
                        "data-testid": Testids.glossary.linkRelation,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Choose…"
                            }),
                            LINK_RELATIONS.map((r)=>/*#__PURE__*/ _jsx("option", {
                                    value: r.value,
                                    children: r.label
                                }, r.value))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Other term or team",
                    placeholder: "Pick a term or team",
                    searchPlaceholder: "Search terms and teams…",
                    options: options,
                    value: to,
                    onValueChange: setTo,
                    validationMessage: from && from === to ? 'Pick two different ends.' : undefined,
                    triggerProps: {
                        'data-testid': Testids.glossary.linkTo
                    }
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Source document",
                    htmlFor: "glossary-link-document",
                    hint: "The page that says so, or leave blank and state the source below.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        id: "glossary-link-document",
                        value: documentId,
                        onChange: (e)=>setDocumentId(e.target.value),
                        "data-testid": Testids.glossary.linkSourceDocument,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "No document"
                            }),
                            documents.map((d)=>/*#__PURE__*/ _jsx("option", {
                                    value: d.id,
                                    children: d.title
                                }, d.id))
                        ]
                    })
                }),
                documentId ? /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Page",
                    htmlFor: "glossary-link-page",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "glossary-link-page",
                        type: "number",
                        min: 1,
                        value: page,
                        onChange: (e)=>setPage(e.target.value),
                        "data-testid": Testids.glossary.linkSourcePage
                    })
                }) : null,
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Stated source",
                    htmlFor: "glossary-link-statement",
                    hint: "Who said it and when, e.g. “client statement, 2026-03-02”. A link needs this or a source document.",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "glossary-link-statement",
                        value: statement,
                        placeholder: "Client statement, 2026-03-02",
                        onChange: (e)=>setStatement(e.target.value),
                        "data-testid": Testids.glossary.linkStatement
                    })
                })
            ]
        })
    });
}
